<template>
  <!-- Map Section -->
  <div class="block-rounded block">
    <div class="block-header block-header-default">
      <h3 class="block-title">{{ report.reference }}</h3>
      <div class="block-options">
      </div>
    </div>
    <div class="block-content block-content-full">
      <div id="map" style="width: 100%; height: 500px;"></div>
    </div>
  </div>

  <!-- Shipments Table Section -->
  <div class="block-rounded block">
    <div class="block-header block-header-default">
      <h3 class="block-title">
        <div>{{ $t('title_result') }} <span>{{ shipments.length }}</span></div>

      </h3>
      <div class="block-options">
        <button type="button" class="btn-block-option">
          <button type="button" class="btn btn-sm btn-primary" @click="saveOrder">{{ $t('button_save') }}</button>
        </button>
      </div>
    </div>
    <div class="block-content block-content-full">
      <div class="table-responsive">
        <div style="min-height: 400px; overflow-y: auto;">
          <table class="table-sm table-vcenter js-dataTable-full fs-sm table-hover table">
            <thead>
              <tr class="text-center">
                <th></th>
                <th>{{ $t('table_reference') }}</th>
                <th style="width: 10%;">{{ $t('table_client') }}</th>
                <th>{{ $t('table_receiver') }}</th>
                <th style="width: 10%; overflow-wrap: break-word;">{{ $t('table_mobile_number') }}</th>
                <th>{{ $t('table_area') }}</th>
                <th>{{ $t('table_address') }}</th>
                <th>{{ $t('table_notes') }}</th>
                <th></th>

              </tr>
            </thead>

            <!-- Draggable should wrap just the tbody -->
            <draggable v-model="sortedShipments" tag="tbody" item-key="_id" @end="updateOrderFromTable">
              <template #item="{ element: shipment, index }">
                <tr class="text-center">
                  <td class="drag-handle">↕</td>
                  <td>
                    <a :href="`/shipments/${shipment._id}`">{{ shipment.reference }}</a>
                  </td>
                  <td>
                    <a v-if="shipment.client_data" :href="`/partner/shipment/details/view/${shipment.client_id}`">
                      {{ shipment.client_data.first_name }} {{ shipment.client_data.last_name }}
                    </a>
                    <span v-else class="text-danger">No Client Data</span>
                  </td>
                  <td>{{ shipment.receiver_name }}</td>
                  <td>
                    <span v-for="(simNumber, simIndex) in shipment.receiver_sim_number" :key="simIndex">
                      {{ simNumber }}
                    </span>
                  </td>
                  <td>
                    <span v-if="shipment.area_data">
                      {{ shipment.area_data.city_name_ar }}, {{ shipment.area_data.governorate?.governorate_name_ar }}
                    </span>
                    <span v-else class="text-danger">No Area</span>
                  </td>
                  <td>{{ shipment.receiver_address }}</td>
                  <td>{{ shipment.notes }}</td>
                  <td style="width: auto">
                    <span @click.stop>
                      <a v-if="shipment.latitude && shipment.longitude"
                        :href="`https://www.google.com/maps/search/?api=1&query=${shipment.latitude},${shipment.longitude}`"
                        target="_blank">
                        <i class="fa fa-map-location-dot fa-lg text-success"></i>
                      </a>
                      <i v-else class="fa fa-map-location-dot fa-lg text-muted" :title="$t('no_location')"></i>
                    </span>
                  </td>
                </tr>
              </template>
            </draggable>
          </table>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import draggable from 'vuedraggable'
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default {
  components: {
    draggable,
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
    shipments: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      sortedShipments: [],
      markers: [],
      routePolyline: null, // Add polyline reference
    }
  },
  mounted() {
    this.initializeSortedShipments();
    this.initMap();
  },
  methods: {
    initializeSortedShipments() {
      this.sortedShipments = [...this.shipments]
        .sort((a, b) => a.order_index - b.order_index);
    },

    initMap() {
      // Initialize map only when DOM is ready
      this.$nextTick(() => {
        this.map = L.map('map').setView([30.0444, 31.2357], 12);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(this.map);
        this.updateMapMarkers();
      });
    },

    updateMapMarkers() {
      // Clear existing markers
      this.markers.forEach(marker => marker.remove());
      this.markers = [];
      const latlngs = [];

      this.sortedShipments.forEach(shipment => {
        if (shipment.latitude && shipment.longitude) {
          const latlng = [shipment.latitude, shipment.longitude];
          latlngs.push(latlng);

          const marker = L.marker(latlng, {
            icon: this.createMarkerIcon(
              shipment.status_data?.hexcolor || '#000000',
              shipment.order_index
            ),
            draggable: false
          });

          // Add tooltip with reference
          marker.bindTooltip(`<strong>${shipment.reference}</strong>`, {
            permanent: false,
            direction: 'top',
            offset: [0, -10],
            className: 'marker-tooltip'
          });

          marker.on('dragend', (e) => this.handleMarkerDrag(shipment, e));
          marker.bindPopup(this.createPopupContent(shipment));
          marker.addTo(this.map);
          this.markers.push(marker);
        }
      });

      // Fit map to markers
      if (latlngs.length > 0) {
        const bounds = L.latLngBounds(latlngs);
        this.map.fitBounds(bounds, {
          padding: [50, 50], // Add padding to keep markers from edge
          maxZoom: 16         // Prevent too close zoom
        });
      }

      console.log(this.sortedShipments);
    },

    createPopupContent(shipment) {
      const vm = this; // Reference to Vue instance
      const container = L.DomUtil.create('div');

      container.innerHTML = `
        <div class="map-popup-controls">
          <button class="btn btn-sm move-up">↑</button>
          <span>${shipment.order_index}</span>
          <button class="btn btn-sm move-down">↓</button>
        </div>
      `;

      container.querySelector('.move-up').addEventListener('click', () => {
        vm.moveShipmentUp(shipment._id);
      });

      container.querySelector('.move-down').addEventListener('click', () => {
        vm.moveShipmentDown(shipment._id);
      });

      return container;
    },

    moveShipmentUp(shipmentId) {
      const index = this.sortedShipments.findIndex(s => s._id === shipmentId);
      if (index > 0) {
        this.swapOrders(index, index - 1);
      }
    },

    moveShipmentDown(shipmentId) {
      const index = this.sortedShipments.findIndex(s => s._id === shipmentId);
      if (index < this.sortedShipments.length - 1) {
        this.swapOrders(index, index + 1);
      }
    },

    swapOrders(oldIndex, newIndex) {
      const tempOrder = this.sortedShipments[oldIndex].order_index;
      this.sortedShipments[oldIndex].order_index =
        this.sortedShipments[newIndex].order_index;
      this.sortedShipments[newIndex].order_index = tempOrder;

      this.sortedShipments.sort((a, b) => a.order_index - b.order_index);
      this.updateMapMarkers();
    },

    updateOrderFromTable() {
      this.sortedShipments.forEach((shipment, index) => {
        shipment.order_index = index + 1;
      });
      this.updateMapMarkers();
    },

    handleMarkerDrag(shipment, event) {
      const newLatLng = event.target.getLatLng();
      shipment.latitude = newLatLng.lat;
      shipment.longitude = newLatLng.lng;
      this.updateMapMarkers(); // Update polyline when marker moves
    },

    async saveOrder() {
      try {
        await axios.put('/delivery-report/shipments/order', {
          shipments: this.sortedShipments.map(s => ({
            id: s._id,
            order_index: s.order_index,
            latitude: s.latitude,
            longitude: s.longitude
          }))
        });
        Swal.fire({
          title: this.$t('message_success'),
          text: 'Order saved successfully',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        Swal.fire({
          title: this.$t('message_error'),
          text: this.$t('message_general_error'),
          icon: 'danger',
          showConfirmButton: false,
          timer: 1500,
        });
        this.$notify.error('Save failed: ' + error.message);
      }
    },

    resetOrder() {
      this.initializeSortedShipments();
      this.updateMapMarkers();
    },

    createMarkerIcon(color, label) {
      return L.divIcon({
        className: 'custom-marker',
        html: `
        <div style="
          background-color: ${color}; 
          color: white; 
          width: 24px; 
          height: 24px; 
          border-radius: 50%; 
          display: flex; 
          align-items: center; 
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          font-weight: bold;
          font-size: 14px;
        ">
          ${label}
        </div>
      `,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
    },
  }
}
</script>


<style>
.drag-handle {
  cursor: move;
  user-select: none;
}

.map-popup-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px;
}

.custom-marker {
  transition: transform 0.3s ease;
}

.table {
  display: table;
  width: 100%;
}

thead {
  display: table-header-group;
}

draggable {
  display: table-row-group;
}

tr {
  display: table-row;
}

td,
th {
  display: table-cell;
}

.drag-handle {
  width: 30px;
}

.leaflet-interactive {
  cursor: pointer;
  transition: stroke-width 0.2s ease;
}

.leaflet-interactive:hover {
  stroke-width: 5;
}

.marker-tooltip {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
}

.marker-tooltip::before {
  border-top-color: rgba(0, 0, 0, 0.7);
}
</style>